export type PulseStatus = 'active' | 'paused';
export type PulseCadence = 'weekly' | 'daily';
export type PulseGeographyScope = 'nation' | 'state' | 'dma' | 'county';
export type PulseVertical =
  | 'local-approvals'
  | 'politics'
  | 'government'
  | 'commercial';

export type PulseConfidenceTier =
  | 'well_measured'
  | 'partially_measured'
  | 'thinly_measured';

export type PulseDirection = 'rising' | 'flat' | 'falling';

export type PulseRunStatus =
  | 'pending'
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'partial'
  | 'failed';

export interface PulseSourceConfig {
  enabled?: boolean;
  weight?: number;
  params?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface PulseEnabledSources {
  [sourceKey: string]: PulseSourceConfig;
}

export interface PulseRunSummary {
  id: string;
  scheduled_for: string;
  started_at: string | null;
  finished_at: string | null;
  status: PulseRunStatus;
  window_start: string;
  window_end: string;
  is_backfill: boolean;
  source_outcomes: Record<string, unknown>;
  duration_seconds: number | null;
}

export interface PulseSnapshotSummary {
  id: string;
  pillar: string;
  value: string | number;
  calibrated_value: string | number | null;
  denominator: number | null;
  denominator_unit: string | null;
  confidence_tier: PulseConfidenceTier;
  direction: PulseDirection;
  window_start: string;
  window_end: string;
  provenance: Record<string, unknown>;
  measurement_type: string;
}

export interface PulseListItem {
  id: string;
  name: string;
  slug: string;
  vertical: PulseVertical;
  construct_version: number;
  cadence: PulseCadence;
  geography_scope: PulseGeographyScope;
  status: PulseStatus;
  created_at: string;
  paused_at: string | null;
  last_run: {
    id: string;
    status: PulseRunStatus;
    window_start: string;
    window_end: string;
    finished_at: string | null;
    is_backfill: boolean;
  } | null;
  position: PulseSnapshotSummary | null;
}

export interface PulseListAggregates {
  active: number;
  paused: number;
  last_tick: string | null;
  backfill_queue: {
    windows: number;
    issue_name: string | null;
  };
}

export interface PulseListResponse {
  pulses: PulseListItem[];
  aggregates: PulseListAggregates;
}

export interface PulseSourceHealth {
  source_key: string;
  status: string;
  items?: number | null;
  detail?: string | null;
  outcome: Record<string, unknown>;
}

export interface PulseDetail {
  id: string;
  name: string;
  slug: string;
  vertical: PulseVertical;
  cadence: PulseCadence;
  geography_scope: PulseGeographyScope;
  geography_ids: string[] | null;
  enabled_sources: PulseEnabledSources;
  status: PulseStatus;
  created_at: string;
  paused_at: string | null;
  relevance_threshold: string | number | null;
  construct: {
    id: string;
    version: number;
    question_text: string;
    construct_definition: string;
    frozen_at: string;
  };
  pillars: {
    salience: PulseSnapshotSummary | null;
    climate: PulseSnapshotSummary | null;
    expectation: PulseSnapshotSummary | null;
    position: PulseSnapshotSummary | null;
  };
  recent_runs: PulseRunSummary[];
  source_health: PulseSourceHealth[];
  list_summary: PulseListItem;
}

export interface PulseSourceCatalogueItem {
  source_key: string;
  pillars: string[];
  shapes: string[];
  supports_backfill: boolean;
  max_backfill_days: number | null;
}

export interface PulseCreateRequest {
  name: string;
  vertical: PulseVertical;
  question_text: string;
  construct_definition: string;
  geography_scope: PulseGeographyScope;
  cadence: PulseCadence;
  enabled_sources: PulseEnabledSources;
  geography_ids?: string[] | null;
  backfill_months?: number;
}

export const PULSE_VERTICAL_OPTIONS: { value: PulseVertical; label: string }[] = [
  { value: 'local-approvals', label: 'Local Approvals' },
  { value: 'politics', label: 'Politics & Policy' },
  { value: 'government', label: 'Government' },
  { value: 'commercial', label: 'Commercial' },
];

export const PULSE_CADENCE_OPTIONS: { value: PulseCadence; label: string }[] = [
  { value: 'weekly', label: 'Weekly — Monday to Monday' },
  { value: 'daily', label: 'Daily' },
];

export const PULSE_GEOGRAPHY_OPTIONS: { value: PulseGeographyScope; label: string }[] = [
  { value: 'nation', label: 'National' },
  { value: 'state', label: 'State' },
  { value: 'dma', label: 'DMA' },
  { value: 'county', label: 'County' },
];

export const PULSE_BACKFILL_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'None' },
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
];
